import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";
import { Trash2, Edit3, Plus, X } from "lucide-react";

const empty = {
  name: "", origin: "", roast_level: "medium", flavor_notes: "", description: "",
  price_inr: 499, image_url: "", arabica_pct: 80, robusta_pct: 20, chicory_pct: 0,
  featured: false, most_popular: false, stock: 100,
};

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("orders");
  const [blends, setBlends] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [saveError, setSaveError] = useState("");

  const refresh = async () => {
    const [b, o, s] = await Promise.all([
      axios.get(`${API}/blends`),
      axios.get(`${API}/admin/orders`),
      axios.get(`${API}/admin/stats`),
    ]);
    setBlends(b.data);
    setOrders(o.data);
    setStats(s.data);
  };

  useEffect(() => { if (user?.role === "admin") refresh(); }, [user]);

  if (loading) return <div className="pt-40 text-center label-mono">Loading…</div>;
  if (!user || user.role !== "admin") {
    return (
      <div className="max-w-[560px] mx-auto px-6 pt-40 pb-24 text-center">
        <div className="label-mono mb-4 text-[var(--accent)]">Restricted</div>
        <h1 className="font-serif text-5xl">Admin only.</h1>
        <p className="mt-4 text-[var(--text-secondary)]">
          Sign in with an admin email. Add your email to <span className="font-mono text-[var(--primary)]">ADMIN_EMAILS</span> in backend .env to grant access.
        </p>
      </div>
    );
  }

  const startEdit = (b) => {
    setSaveError("");
    setEditing(b.id);
    setForm({ ...b, flavor_notes: b.flavor_notes.join(", ") });
    setShowForm(true);
  };
  const startNew = () => { setSaveError(""); setEditing(null); setForm(empty); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaveError("");
    const payload = {
      ...form,
      price_inr: +form.price_inr,
      arabica_pct: +form.arabica_pct,
      robusta_pct: +form.robusta_pct,
      chicory_pct: +form.chicory_pct,
      stock: +form.stock,
      flavor_notes: form.flavor_notes.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) await axios.put(`${API}/blends/${editing}`, payload);
      else await axios.post(`${API}/blends`, payload);
      setShowForm(false);
      await refresh();
    } catch (err) {
      setSaveError(err?.response?.data?.detail || err?.message || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this blend?")) return;
    await axios.delete(`${API}/blends/${id}`);
    await refresh();
  };

  const setStatus = async (id, status) => {
    await axios.put(`${API}/admin/orders/${id}/status`, { status });
    await refresh();
  };

  const setPayment = async (id, payment_status) => {
    await axios.put(`${API}/admin/orders/${id}/payment`, { payment_status });
    await refresh();
  };

  return (
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-32 pb-24">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <div>
          <div className="label-mono mb-3 text-[var(--primary)]">Control Room</div>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tighter">The Roastery Dashboard.</h1>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          <Stat k="Orders" v={stats.total_orders} />
          <Stat k="Revenue" v={`₹${stats.revenue_inr}`} accent />
          <Stat k="Pending" v={stats.pending} />
          <Stat k="Shipped" v={stats.shipped} />
          <Stat k="Delivered" v={stats.delivered} />
        </div>
      )}

      <div className="flex gap-2 mb-6 border-b border-[var(--border)]">
        {["orders", "blends"].map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`label-mono px-4 py-3 border-b-2 -mb-px ${tab === t ? "border-[var(--primary)] text-[var(--primary)]" : "border-transparent text-[var(--text-secondary)]"}`} data-testid={`admin-tab-${t}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "orders" && (
        <div className="border border-[var(--border)] bg-[var(--surface)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="label-mono text-[var(--text-muted)]">
              <tr className="border-b border-[var(--border)]">
                <th className="text-left px-4 py-3">Order</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Items</th>
                <th className="text-left px-4 py-3">Payment</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Total</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-[var(--border)] hover:bg-[var(--surface-2)]" data-testid={`admin-order-${o.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{o.id}<div className="text-[var(--text-muted)] mt-1">{new Date(o.created_at).toLocaleString()}</div></td>
                  <td className="px-4 py-3">{o.customer_name}<div className="text-[var(--text-muted)] text-xs">{o.email}</div></td>
                  <td className="px-4 py-3">{o.items.length}</td>
                  <td className="px-4 py-3">
                    <div className="uppercase label-mono text-[10px] mb-1">{o.payment_method}</div>
                    <select value={o.payment_status} onChange={(e) => setPayment(o.id, e.target.value)} className="input-dark py-1 text-xs" data-testid={`payment-status-${o.id}`}>
                      {["pending", "paid", "failed", "refunded", "cod-pending", "upi-pending-verification"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {o.upi_reference && (
                      <div className="font-mono text-[9px] mt-1 text-[var(--primary)]">UTR: {o.upi_reference}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <select value={o.status} onChange={(e) => setStatus(o.id, e.target.value)} className="input-dark py-1" data-testid={`order-status-${o.id}`}>
                      {["placed", "processing", "shipped", "delivered", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[var(--primary)]">₹{o.total}</td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-[var(--text-secondary)]">No orders yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "blends" && (
        <>
          <div className="flex justify-end mb-4">
            <button className="btn-primary" onClick={startNew} data-testid="admin-add-blend"><Plus size={14} className="inline mr-1" /> Add Blend</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {blends.map((b) => (
              <div key={b.id} className="border border-[var(--border)] bg-[var(--surface)] p-4">
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <div className="font-serif text-xl flex items-center gap-2">
                      {b.name}
                      {b.most_popular && (
                        <span className="text-[9px] tracking-[0.2em] px-1.5 py-0.5 whitespace-nowrap" style={{background:"linear-gradient(180deg,#FF5C5F,#E5383F)",color:"#FFFFFF",border:"1px solid #7A0F1F"}} data-testid={`admin-popular-tag-${b.id}`}>★ POPULAR</span>
                      )}
                      {b.featured && (
                        <span className="text-[9px] tracking-[0.2em] px-1.5 py-0.5 border border-[var(--border-strong)] text-[var(--text-muted)]">FEATURED</span>
                      )}
                    </div>
                    <div className="label-mono text-[10px] mt-1 text-[var(--text-muted)]">{b.origin} · {b.roast_level}</div>
                    <div className="font-mono text-sm text-[var(--primary)] mt-2">₹{b.price_inr}</div>
                    <div className="label-mono text-[10px] mt-1 text-[var(--text-muted)]">Stock: {b.stock}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => startEdit(b)} className="p-2 border border-[var(--border)] hover:border-[var(--primary)]" data-testid={`edit-${b.id}`}><Edit3 size={14} /></button>
                    <button onClick={() => del(b.id)} className="p-2 border border-[var(--border)] hover:border-[var(--accent)]" data-testid={`delete-${b.id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {showForm && (
            <div className="fixed inset-0 z-[80] flex items-start md:items-center justify-center p-4 pt-28 overflow-y-auto">
              <div className="absolute inset-0 backdrop-blur-sm" style={{ background: "rgba(0,0,0,0.35)" }} onClick={() => setShowForm(false)} />
              <form onSubmit={save} className="relative w-full max-w-2xl bg-[var(--surface)] border border-[var(--border)] p-6 md:p-8 my-8" data-testid="blend-form">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <div className="label-mono">{editing ? "Edit Blend" : "New Blend"}</div>
                    <div className="font-serif text-3xl mt-1">{form.name || "Untitled"}</div>
                  </div>
                  <button type="button" onClick={() => setShowForm(false)}><X size={20} /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Name" v={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                  <Field label="Origin" v={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} required />
                  <div>
                    <div className="label-mono mb-2">Roast</div>
                    <select className="input-dark" value={form.roast_level} onChange={(e) => setForm({ ...form, roast_level: e.target.value })}>
                      <option value="light">Light</option><option value="medium">Medium</option><option value="dark">Dark</option>
                    </select>
                  </div>
                  <Field label="Price (₹ per 250g)" type="number" v={form.price_inr} onChange={(e) => setForm({ ...form, price_inr: e.target.value })} required />
                  <Field label="Arabica %" type="number" v={form.arabica_pct} onChange={(e) => setForm({ ...form, arabica_pct: e.target.value })} />
                  <Field label="Robusta %" type="number" v={form.robusta_pct} onChange={(e) => setForm({ ...form, robusta_pct: e.target.value })} />
                  <Field label="Chicory %" type="number" v={form.chicory_pct} onChange={(e) => setForm({ ...form, chicory_pct: e.target.value })} />
                  <Field label="Stock" type="number" v={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
                  <Field label="Image URL" v={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
                  <div className="md:col-span-2"><Field label="Flavor Notes (comma-separated)" v={form.flavor_notes} onChange={(e) => setForm({ ...form, flavor_notes: e.target.value })} /></div>
                  <div className="md:col-span-2">
                    <div className="label-mono mb-2">Description</div>
                    <textarea className="input-dark min-h-24" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                  </div>
                  <label className="flex items-center gap-3 md:col-span-2">
                    <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="blend-featured-toggle" />
                    <span className="label-mono">Featured on landing</span>
                  </label>
                  <label className="flex items-center gap-3 md:col-span-2">
                    <input type="checkbox" checked={!!form.most_popular} onChange={(e) => setForm({ ...form, most_popular: e.target.checked })} data-testid="blend-most-popular-toggle" />
                    <span className="label-mono">Most Popular <span className="text-[var(--text-muted)] normal-case tracking-normal text-[9px]">(red badge + announcement bar)</span></span>
                  </label>
                </div>
                <div className="flex gap-3 mt-8 items-center">
                  <button type="submit" className="btn-primary" data-testid="save-blend">Save Blend</button>
                  <button type="button" onClick={() => setShowForm(false)} className="btn-outline">Cancel</button>
                  {saveError && (
                    <span className="label-mono text-[var(--accent)] text-xs" data-testid="blend-save-error">{saveError}</span>
                  )}
                </div>
              </form>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ k, v, accent }) {
  return (
    <div className="border border-[var(--border)] bg-[var(--surface)] p-5">
      <div className="label-mono text-[10px]">{k}</div>
      <div className={`font-mono text-2xl mt-2 ${accent ? "text-[var(--primary)]" : ""}`}>{v}</div>
    </div>
  );
}
function Field({ label, v, onChange, required, type = "text" }) {
  return (
    <label className="block">
      <span className="label-mono block mb-2">{label}{required && " *"}</span>
      <input className="input-dark" type={type} value={v} onChange={onChange} required={required} />
    </label>
  );
}
